import Highcharts from 'highcharts/highstock';
import {PlainObject} from '@xh/hoist/core';
import {isArray, isBoolean, isNil, isNumber, isPlainObject, sortBy, startCase} from 'lodash';
import {IndicatorParam, TechnicalIndicator} from '../../../data/ChartTypes';

/**
 * The set of technical indicators offered by the chart widgets, together with the parameters each
 * one exposes for tuning.
 *
 * Rather than maintaining a hand-written catalog of the ~48 indicators shipped by
 * `highcharts/indicators/indicators-all`, we read them back off the Highcharts series registry.
 * Labels come from each indicator's `nameBase`, and the tunable parameters from its
 * `defaultOptions.params` - the same source Highcharts' own Stock Tools popup introspects. This
 * keeps us automatically in sync with whatever the installed Highcharts version supports.
 *
 * The one thing Highcharts does *not* declare is whether an indicator draws on the price axis or
 * needs a pane of its own, so that classification is maintained here.
 */

/** Indicators drawn over the price series. Anything else registered is treated as an oscillator. */
const OVERLAY_TYPES = new Set([
    'abands',
    'bb',
    'dema',
    'ema',
    'ikh',
    'keltnerchannels',
    'linearRegression',
    'pc',
    'pivotpoints',
    'priceenvelopes',
    'psar',
    'sma',
    'supertrend',
    'tema',
    'trendline',
    'vbp',
    'vwap',
    'wma',
    'zigzag'
]);

/** Highcharts registers the regression indicators under a camelCase and an all-lowercase alias. */
const ALIAS_TYPES = new Set([
    'linearregression',
    'linearregressionangle',
    'linearregressionintercept',
    'linearregressionslope'
]);

/** Params wired up by the chart itself, not offered to the user. */
const INTERNAL_PARAMS = new Set(['volumeSeriesID']);

/** Params whose value is chosen from a fixed set rather than typed in. */
const PARAM_OPTIONS: Record<string, {value: any; label: string}[]> = {
    index: [
        {value: 0, label: 'Open'},
        {value: 1, label: 'High'},
        {value: 2, label: 'Low'},
        {value: 3, label: 'Close'}
    ],
    average: [
        {value: 'sma', label: 'SMA'},
        {value: 'ema', label: 'EMA'},
        {value: 'dema', label: 'DEMA'},
        {value: 'tema', label: 'TEMA'},
        {value: 'wma', label: 'WMA'}
    ],
    algorithm: [
        {value: 'standard', label: 'Standard'},
        {value: 'fibonacci', label: 'Fibonacci'},
        {value: 'camarilla', label: 'Camarilla'}
    ]
};

/** Friendlier labels than `startCase()` produces for the more cryptic param names. */
const PARAM_LABELS: Record<string, string> = {
    algorithm: 'Algorithm',
    average: 'Average',
    decimals: 'Decimals',
    factor: 'Factor',
    fastAvgPeriod: 'Fast Average Period',
    index: 'Based On',
    longPeriod: 'Long Period',
    multiplierATR: 'ATR Multiplier',
    period: 'Period',
    periodATR: 'ATR Period',
    periodSenkouSpanB: 'Senkou Span B Period',
    periodTenkan: 'Tenkan Period',
    periods: 'Periods',
    shortPeriod: 'Short Period',
    signalPeriod: 'Signal Period',
    slowAvgPeriod: 'Slow Average Period',
    standardDeviation: 'Standard Deviation',
    xAxisUnit: 'X Axis Unit'
};

/**
 * The registry of series types, keyed by type. Present on the Highcharts namespace at runtime
 * (`Globals.seriesTypes`) but missing from its published typings.
 */
function seriesTypes(): PlainObject {
    return (Highcharts as unknown as {seriesTypes: PlainObject}).seriesTypes ?? {};
}

/**
 * An indicator's `defaultOptions` is a *static* on the series class - `registerSeriesType()` reads
 * it from there and never copies it onto the prototype.
 */
function defaultParams(type: string): PlainObject {
    return seriesTypes()[type]?.defaultOptions?.params;
}

/**
 * Display name for an indicator. Highcharts falls back to the uppercased type when a series
 * declares no `nameBase` of its own (see `SMAIndicator.getName()`), which is what the acronym
 * indicators - RSI, MACD, ATR and friends - rely on. Only an *own* property counts, so that an
 * indicator extending another (NATR from ATR, TEMA from EMA) never inherits its parent's label.
 */
function indicatorLabel(type: string): string {
    const proto = seriesTypes()[type]?.prototype;
    return Object.prototype.hasOwnProperty.call(proto ?? {}, 'nameBase')
        ? proto.nameBase
        : type.toUpperCase();
}

let cachedIndicators: TechnicalIndicator[] = null;

/**
 * All indicators available for selection, sorted by label.
 *
 * Built lazily on first access - the Highcharts indicator modules are imported by `Bootstrap`, so
 * the registry is not guaranteed to be populated at module-evaluation time.
 */
export function getIndicators(): TechnicalIndicator[] {
    if (!cachedIndicators) {
        const types = seriesTypes(),
            found: TechnicalIndicator[] = [];

        Object.keys(types).forEach(type => {
            if (ALIAS_TYPES.has(type)) return;
            // `params` is what distinguishes an indicator from an ordinary series type.
            if (!defaultParams(type)) return;

            found.push({
                value: type,
                label: indicatorLabel(type),
                panel: OVERLAY_TYPES.has(type) ? 'overlay' : 'oscillator'
            });
        });

        cachedIndicators = sortBy(found, it => it.label.toLowerCase());
    }

    return cachedIndicators;
}

/** Returns `undefined` for `null`/empty and for any legacy 'none' placeholder value. */
export function getIndicator(value: string): TechnicalIndicator {
    return getIndicators().find(indicator => indicator.value === value);
}

/** True if the given value resolves to a real, drawable indicator. */
export function isActiveIndicator(value: string): boolean {
    return !!getIndicator(value);
}

/**
 * The tunable parameters exposed by the given indicator, derived from its Highcharts defaults.
 * Returns an empty array for indicators that take no parameters (e.g. AO).
 */
export function getIndicatorParams(value: string): IndicatorParam[] {
    const params = defaultParams(value);
    if (!params) return [];

    const ret: IndicatorParam[] = [];
    Object.keys(params).forEach(name => {
        const defaultValue = params[name];

        // Indicators explicitly set inherited-but-unused params to `undefined` so that they are
        // not shown or applied - see the `#15362` comments throughout the Highcharts source.
        if (isNil(defaultValue) && name !== 'xAxisUnit') return;
        if (INTERNAL_PARAMS.has(name)) return;
        // Nested objects (e.g. `styles`) are presentation, not calculation - skip them.
        if (isPlainObject(defaultValue)) return;

        const label = PARAM_LABELS[name] ?? startCase(name),
            options = PARAM_OPTIONS[name];

        if (options) {
            ret.push({name, label, type: 'select', defaultValue, options});
        } else if (isArray(defaultValue)) {
            ret.push({
                name,
                label,
                type: 'numberList',
                defaultValue,
                length: defaultValue.length
            });
        } else if (isBoolean(defaultValue)) {
            ret.push({name, label, type: 'boolean', defaultValue});
        } else if (isNumber(defaultValue) || isNil(defaultValue)) {
            ret.push({name, label, type: 'number', defaultValue});
        }
    });

    return ret;
}

/** Default param values for the given indicator, as a plain `{name: value}` object. */
export function getDefaultIndicatorParams(value: string): PlainObject {
    const ret: PlainObject = {};
    getIndicatorParams(value).forEach(({name, defaultValue}) => (ret[name] = defaultValue));
    return ret;
}
